import { Check, Search, X } from 'lucide-react';
import { useMemo, useState } from 'react';

import { AdminOrgPicker } from '@apps/web/components/admin/admin_org_picker';
import { Button } from '@apps/web/components/ui/button';
import { Combobox } from '@apps/web/components/ui/combobox';
import {
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from '@apps/web/components/ui/command';
import { Input } from '@apps/web/components/ui/input';
import { MultiSelect } from '@apps/web/components/ui/multi_select';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@apps/web/components/ui/select';
import { Switch } from '@apps/web/components/ui/switch';
import { useAdminAgentScratchpadScopeRegistry } from '@apps/web/features/admin_agent_scratchpad/api/queries';
import {
    FRESHNESS_BUCKETS,
    FRESHNESS_LABELS,
    type FreshnessBucket,
    SCOPE_LABELS,
    SCOPE_TYPES,
    type ScopeType,
    SORT_FIELDS,
    SORT_LABELS,
    type SortField,
    VOLATILITY_LABELS,
    VOLATILITY_VALUES,
    type VolatilityFilter,
} from '@apps/web/features/admin_agent_scratchpad/model/constants';
import {
    normalizeScopeTypes,
    normalizeTags,
    type ScratchpadSearch,
    type ScratchpadSearchPatch,
    serializeScopeTypes,
    singleNonGlobalScopeKind,
} from '@apps/web/features/admin_agent_scratchpad/model/search_params';
import { cn } from '@apps/web/lib/utils';

const FILTER_TRIGGER_CLASS = 'h-8 w-full px-3 text-xs';
const FILTER_GRID_CLASS =
    'grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,1fr)_minmax(0,1fr)]';

/** Returns true when any URL filter is non-default. Anchors the Clear button. */
function hasActiveFilters(search: ScratchpadSearch): boolean {
    return (
        (search.scope_type?.length ?? 0) > 0 ||
        search.scope_id !== undefined ||
        (search.tag?.length ?? 0) > 0 ||
        search.volatility !== undefined ||
        search.freshness !== undefined ||
        search.include_retired === true ||
        search.cross_scope === true ||
        (search.search ?? '') !== ''
    );
}

export function ScratchpadFilterBar({
    search,
    availableTags,
    searchDraft,
    onSearchDraftChange,
    onClearSearchDraft,
    onPatch,
}: {
    readonly search: ScratchpadSearch;
    readonly availableTags: ReadonlyArray<string>;
    readonly searchDraft: string;
    readonly onSearchDraftChange: (value: string) => void;
    readonly onClearSearchDraft: () => void;
    readonly onPatch: (patch: ScratchpadSearchPatch) => void;
}) {
    const tags = normalizeTags(search);
    const activeFilters = hasActiveFilters(search);
    const selectedScopeTypes = normalizeScopeTypes(search);
    const singleKind = singleNonGlobalScopeKind(search);
    const tagOptions = mergeAvailableAndSelectedTags(availableTags, tags);

    // The scope-type list drives whether scope_id makes sense: it's only
    // meaningful when exactly one non-global kind is selected. Any change
    // that invalidates that pairing clears scope_id in the same patch to
    // keep URL state coherent with what the API accepts.
    function updateScopeTypes(nextValues: ReadonlyArray<string>) {
        const parsed = nextValues.filter((value): value is ScopeType =>
            (SCOPE_TYPES as ReadonlyArray<string>).includes(value),
        );
        const dedupedNext = Array.from(new Set(parsed));
        const nextSingle = dedupedNextSingleKind(dedupedNext);
        const scopeIdPatch =
            !dedupedNext.includes('global') &&
            nextSingle !== null &&
            nextSingle === singleKind
                ? {}
                : { scope_id: undefined };
        onPatch({
            scope_type: serializeScopeTypes(dedupedNext),
            ...scopeIdPatch,
            page: 1,
        });
    }

    return (
        <div className="border-border/60 bg-card/40 flex flex-col gap-3 rounded-xl border p-3 shadow-[0_1px_0_rgba(15,23,42,0.03)] backdrop-blur">
            <div className="relative">
                <Search
                    className="text-muted-foreground pointer-events-none absolute top-1/2 left-3 h-3.5 w-3.5 -translate-y-1/2"
                    aria-hidden="true"
                />
                <Input
                    value={searchDraft}
                    placeholder="Search scratchpad — title, one-liner, body…"
                    className="h-8 pl-8 text-xs tracking-tight"
                    aria-label="Search scratchpad"
                    onChange={(event) => {
                        onSearchDraftChange(event.target.value);
                    }}
                />
                {searchDraft.length > 0 ? (
                    <button
                        type="button"
                        aria-label="Clear search"
                        onClick={onClearSearchDraft}
                        className="text-muted-foreground hover:text-foreground absolute top-1/2 right-2 -translate-y-1/2 transition-colors"
                    >
                        <X className="h-3.5 w-3.5" />
                    </button>
                ) : null}
            </div>
            <div className={FILTER_GRID_CLASS}>
                <MultiSelect
                    options={SCOPE_TYPES.map((kind) => ({
                        value: kind,
                        label: SCOPE_LABELS[kind],
                    }))}
                    selected={[...selectedScopeTypes]}
                    onChange={updateScopeTypes}
                    placeholder="Any scope"
                    compactLabel={compactScopeLabel(selectedScopeTypes)}
                    matchTriggerWidth={true}
                    className={FILTER_TRIGGER_CLASS}
                />
                <ScopeIdControl
                    singleKind={singleKind}
                    value={search.scope_id}
                    disabledReason={resolveScopeIdDisabledReason({
                        selectedScopeTypes,
                        singleKind,
                    })}
                    className={FILTER_TRIGGER_CLASS}
                    onChange={(value) => {
                        onPatch({ scope_id: value, page: 1 });
                    }}
                />
                <MultiSelect
                    options={tagOptions.map((tag) => ({
                        value: tag,
                        label: tag,
                    }))}
                    selected={[...tags]}
                    onChange={(values) => {
                        onPatch({
                            tag: values.length === 0 ? undefined : values,
                            page: 1,
                        });
                    }}
                    placeholder="All tags"
                    matchTriggerWidth={true}
                    className={FILTER_TRIGGER_CLASS}
                />
                <Select
                    value={search.volatility ?? '__all__'}
                    onValueChange={(value) => {
                        onPatch({
                            volatility:
                                value === '__all__'
                                    ? undefined
                                    : (value as VolatilityFilter),
                            page: 1,
                        });
                    }}
                >
                    <SelectTrigger size="sm" className={FILTER_TRIGGER_CLASS}>
                        <SelectValue placeholder="Volatility" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">All trust</SelectItem>
                        {VOLATILITY_VALUES.map((volatility) => (
                            <SelectItem key={volatility} value={volatility}>
                                {VOLATILITY_LABELS[volatility]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select
                    value={search.freshness ?? '__all__'}
                    onValueChange={(value) => {
                        onPatch({
                            freshness:
                                value === '__all__'
                                    ? undefined
                                    : (value as FreshnessBucket),
                            page: 1,
                        });
                    }}
                >
                    <SelectTrigger size="sm" className={FILTER_TRIGGER_CLASS}>
                        <SelectValue placeholder="Freshness" />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="__all__">Any time</SelectItem>
                        {FRESHNESS_BUCKETS.map((freshness) => (
                            <SelectItem key={freshness} value={freshness}>
                                {FRESHNESS_LABELS[freshness]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
            </div>
            <div className="border-border/50 flex flex-wrap items-center gap-2 border-t pt-2">
                <Select
                    value={search.sort ?? 'updated_at'}
                    onValueChange={(value) => {
                        onPatch({
                            sort: value as SortField,
                            page: 1,
                        });
                    }}
                >
                    <SelectTrigger
                        size="sm"
                        className={cn(FILTER_TRIGGER_CLASS, 'w-[11rem]')}
                    >
                        <SelectValue placeholder="Sort by" />
                    </SelectTrigger>
                    <SelectContent>
                        {SORT_FIELDS.map((field) => (
                            <SelectItem key={field} value={field}>
                                {SORT_LABELS[field]}
                            </SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <label className="border-border/50 bg-muted/20 text-muted-foreground inline-flex h-8 items-center gap-2 rounded-md border px-3 text-xs">
                    <Switch
                        checked={search.include_retired === true}
                        onCheckedChange={(checked) => {
                            onPatch({
                                include_retired: checked ? true : undefined,
                                page: 1,
                            });
                        }}
                    />
                    <span>Show retired</span>
                </label>
                <label
                    className="border-border/50 bg-muted/20 text-muted-foreground inline-flex h-8 items-center gap-2 rounded-md border px-3 text-xs"
                    title="Search across all organizations (default: current scope only)."
                >
                    <Switch
                        checked={search.cross_scope === true}
                        onCheckedChange={(checked) => {
                            // cross_scope still applies a supplied scope_id;
                            // preserve it so enabling this flag does not
                            // silently change the active filter.
                            onPatch({
                                cross_scope: checked ? true : undefined,
                                page: 1,
                            });
                        }}
                    />
                    <span>Cross-scope</span>
                </label>
                {activeFilters ? (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="ml-auto text-xs"
                        onClick={() => {
                            // Clearing lands on the same valid default the
                            // bare route uses (global scope, no scope_id, no
                            // cross_scope). The hook re-adds scope_type=global
                            // implicitly, so leaving it undefined here keeps
                            // the URL clean.
                            onPatch({
                                page: 1,
                                scope_type: undefined,
                                scope_id: undefined,
                                tag: undefined,
                                volatility: undefined,
                                freshness: undefined,
                                include_retired: undefined,
                                cross_scope: undefined,
                                search: undefined,
                                sort: undefined,
                                direction: undefined,
                            });
                        }}
                    >
                        Clear filters
                    </Button>
                ) : null}
            </div>
        </div>
    );
}

function dedupedNextSingleKind(
    dedupedNext: ReadonlyArray<ScopeType>,
): Exclude<ScopeType, 'global'> | null {
    const specific = dedupedNext.filter(
        (kind): kind is Exclude<ScopeType, 'global'> => kind !== 'global',
    );
    return specific.length === 1 ? (specific[0] ?? null) : null;
}

function compactScopeLabel(
    selected: ReadonlyArray<ScopeType>,
): string | undefined {
    if (selected.length === 0) {
        return 'Any scope';
    }
    if (selected.length === 1) {
        return SCOPE_LABELS[selected[0] as ScopeType];
    }
    return `${selected.length} scopes`;
}

type ScopeIdDisabledReason =
    | 'none-selected'
    | 'global-only'
    | 'global-selected'
    | 'multi-kind'
    | null;

function resolveScopeIdDisabledReason({
    selectedScopeTypes,
    singleKind,
}: {
    readonly selectedScopeTypes: ReadonlyArray<ScopeType>;
    readonly singleKind: Exclude<ScopeType, 'global'> | null;
}): ScopeIdDisabledReason {
    if (selectedScopeTypes.length === 0) {
        return 'none-selected';
    }
    if (selectedScopeTypes.includes('global')) {
        return 'global-selected';
    }
    if (singleKind !== null) {
        return null;
    }
    // No non-global kind resolves. Distinguish "only global selected" (which
    // legitimately has no scope_id) from "multiple non-global kinds selected"
    // (which cannot share a single id) so the placeholder copy is honest.
    const hasNonGlobal = selectedScopeTypes.some((kind) => kind !== 'global');
    return hasNonGlobal ? 'multi-kind' : 'global-only';
}

function ScopeIdControl({
    singleKind,
    value,
    disabledReason,
    className,
    onChange,
}: {
    readonly singleKind: Exclude<ScopeType, 'global'> | null;
    readonly value: string | undefined;
    readonly disabledReason: ScopeIdDisabledReason;
    readonly className: string;
    readonly onChange: (value: string | undefined) => void;
}) {
    if (disabledReason !== null) {
        const label =
            disabledReason === 'global-selected'
                ? 'No scope id (global selected)'
                : disabledReason === 'multi-kind'
                  ? 'Multiple scope kinds — no id'
                  : disabledReason === 'global-only'
                    ? 'No scope id (global)'
                    : 'Pick a scope kind to filter by id';
        const ariaLabel =
            disabledReason === 'global-selected'
                ? 'Scope id disabled — global scope selected'
                : disabledReason === 'multi-kind'
                  ? 'Scope id disabled — multiple scope kinds selected'
                  : disabledReason === 'global-only'
                    ? 'Global scope has no id'
                    : 'Scope id disabled — no scope kind selected';
        if (value !== undefined && value !== '') {
            return (
                <div
                    className={cn(
                        'border-border/60 bg-muted/30 text-muted-foreground inline-flex items-center gap-2 rounded-md border',
                        className,
                    )}
                >
                    <span className="min-w-0 flex-1 truncate font-mono">
                        {value}
                    </span>
                    <button
                        type="button"
                        aria-label="Clear scope id"
                        className="text-foreground underline underline-offset-2"
                        onClick={() => {
                            onChange(undefined);
                        }}
                    >
                        Clear
                    </button>
                </div>
            );
        }
        return (
            <button
                type="button"
                disabled={true}
                aria-disabled={true}
                aria-label={ariaLabel}
                className={cn(
                    'border-border/60 bg-muted/30 text-muted-foreground/70 inline-flex cursor-not-allowed items-center rounded-md border',
                    className,
                )}
            >
                {label}
            </button>
        );
    }
    if (singleKind === 'organization') {
        return (
            <AdminOrgPicker
                value={value}
                triggerClassName={className}
                onValueChange={(next) => {
                    onChange(next);
                }}
            />
        );
    }
    if (singleKind === null) {
        return null;
    }
    return (
        <RegistryScopeIdPicker
            key={singleKind}
            kind={singleKind}
            value={value}
            className={className}
            onChange={onChange}
        />
    );
}

function RegistryScopeIdPicker({
    kind,
    value,
    className,
    onChange,
}: {
    readonly kind: 'tool' | 'skill';
    readonly value: string | undefined;
    readonly className: string;
    readonly onChange: (value: string | undefined) => void;
}) {
    const { data, isLoading } = useAdminAgentScratchpadScopeRegistry();
    const registryNames = useMemo<ReadonlyArray<string>>(() => {
        if (data === undefined) {
            return [];
        }
        return kind === 'tool' ? data.tools : data.skills;
    }, [data, kind]);
    const [open, setOpen] = useState(false);
    const [searchValue, setSearchValue] = useState('');
    const filtered = useMemo(() => {
        const normalized = searchValue.trim().toLowerCase();
        if (normalized === '') {
            return registryNames.slice(0, 200);
        }
        return registryNames
            .filter((name) => name.toLowerCase().includes(normalized))
            .slice(0, 200);
    }, [registryNames, searchValue]);

    // If the URL holds a scope_id that vanished from the registry (renamed
    // tool, deleted skill), surface it in the display so the user knows the
    // filter still applies — clearing is a click away.
    const displayValue =
        value !== undefined && value !== '' ? (
            <span className="font-mono text-xs">{value}</span>
        ) : undefined;
    const placeholder = kind === 'tool' ? 'Pick a tool' : 'Pick a skill';

    return (
        <div className="w-full">
            <div>
                <Combobox
                    open={open}
                    onOpenChange={setOpen}
                    displayValue={displayValue}
                    placeholder={placeholder}
                    searchValue={searchValue}
                    onSearchValueChange={setSearchValue}
                    searchPlaceholder={
                        kind === 'tool'
                            ? 'Search tool names…'
                            : 'Search skill names…'
                    }
                    triggerClassName={className}
                    triggerProps={{
                        'data-testid': `scratchpad-scope-${kind}-trigger`,
                    }}
                >
                    <CommandEmpty>
                        {isLoading ? 'Loading registry…' : 'No matches.'}
                    </CommandEmpty>
                    <CommandGroup>
                        {filtered.map((name) => {
                            const isSelected = name === value;
                            return (
                                <CommandItem
                                    key={name}
                                    value={name}
                                    onSelect={() => {
                                        onChange(name);
                                        setOpen(false);
                                    }}
                                >
                                    <Check
                                        className={cn(
                                            'mr-1 h-3 w-3',
                                            isSelected
                                                ? 'opacity-100'
                                                : 'opacity-0',
                                        )}
                                    />
                                    <span className="font-mono text-xs">
                                        {name}
                                    </span>
                                </CommandItem>
                            );
                        })}
                    </CommandGroup>
                </Combobox>
            </div>
            {value !== undefined && value !== '' ? (
                <button
                    type="button"
                    aria-label={`Clear ${kind} scope`}
                    className="text-muted-foreground hover:text-foreground rounded-sm p-1 transition-colors"
                    onClick={() => {
                        onChange(undefined);
                    }}
                >
                    <X className="h-3 w-3" />
                </button>
            ) : null}
        </div>
    );
}

function mergeAvailableAndSelectedTags(
    available: ReadonlyArray<string>,
    selected: ReadonlyArray<string>,
): Array<string> {
    const seen = new Set<string>();
    const merged: Array<string> = [];
    for (const tag of available) {
        if (!seen.has(tag)) {
            seen.add(tag);
            merged.push(tag);
        }
    }
    for (const tag of selected) {
        if (!seen.has(tag)) {
            seen.add(tag);
            merged.push(tag);
        }
    }
    return merged;
}
